import React, { useState, useEffect } from 'react';
import { useParams, Link, useLocation, useNavigate } from 'react-router-dom';
import { Row, Col, Card, Badge, Spinner, Alert, Button, Modal, Collapse } from 'react-bootstrap';
import HTMLView from '../components/Layout/HTMLView';
import ShareComponent from '../components/Common/ShareComponent';
import AddToCalendarComponent from '../components/Common/AddToCalendarComponent';
import { getSingleConference } from '../services/api';
import AttendeeRegistrationModal from '../components/Payments/AttendeeRegistrationModal.jsx';
import { calculateFeeBreakdown } from '../utils/registrationFee';

const Conference = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [conference, setConference] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedImage, setSelectedImage] = useState(null);
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);
    const [showImageModal, setShowImageModal] = useState(false);
    const [showRegistrationModal, setShowRegistrationModal] = useState(false);
    const [programExpanded, setProgramExpanded] = useState(false);

    const baseUrl = process.env.REACT_APP_API_URL || '/api';
    const LOCALE = 'en-GB';

    useEffect(() => {
        // Add CSS for responsive images in conference descriptions
        const style = document.createElement('style');
        style.textContent = `
          .card-body img {
            max-width: 100% !important;
            height: auto !important;
            width: 100% !important;
          }
        `;
        document.head.appendChild(style);

        return () => {
          // Cleanup: remove the style when component unmounts
          if (document.head.contains(style)) {
            document.head.removeChild(style);
          }
        };
      }, []);

    useEffect(() => {
        const fetchConference = async () => {
            try {
                setLoading(true);
                const response = await getSingleConference(id);
                setConference(response);
                setError(null);
            } catch (err) {
                console.error('Error fetching conference:', err);
                setError('Failed to load conference details. Please try again later.');
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchConference();
        }
    }, [id]);

    const galleryPhotos = conference?.photos || [];

    const handleImageClick = (photo, index) => {
        setSelectedImage(photo);
        setSelectedImageIndex(index);
        setShowImageModal(true);
    };

    const closeImageModal = () => {
        setShowImageModal(false);
        setSelectedImage(null);
        setSelectedImageIndex(0);
    };

    const handleNavigateImage = (direction) => {
        if (!galleryPhotos.length) return;

        const nextIndex = (selectedImageIndex + direction + galleryPhotos.length) % galleryPhotos.length;
        setSelectedImageIndex(nextIndex);
        setSelectedImage(galleryPhotos[nextIndex]);
    };

    useEffect(() => {
        if (!showImageModal) return;

        const handleKeyDown = (event) => {
            if (event.key === 'ArrowRight') {
                event.preventDefault();
                handleNavigateImage(1);
            }
            if (event.key === 'ArrowLeft') {
                event.preventDefault();
                handleNavigateImage(-1);
            }
            if (event.key === 'Escape') {
                event.preventDefault();
                closeImageModal();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [showImageModal, selectedImageIndex, galleryPhotos]);

    const getStatusBadgeVariant = (status) => {
        switch (status) {
            case 'Active': return 'success';
            case 'Open': return 'primary';
            case 'Close': return 'warning';
            case 'Ended': return 'secondary';
            default: return 'info';
        }
    };

    const getFileIcon = (filename, mimetype) => {
        // Use mimetype first, then fallback to filename extension
        if (mimetype) {
            if (mimetype.includes('pdf')) return 'bi-file-earmark-pdf text-danger';
            if (mimetype.includes('word') || mimetype.includes('document')) return 'bi-file-earmark-word text-primary';
            if (mimetype.includes('excel') || mimetype.includes('spreadsheet')) return 'bi-file-earmark-excel text-success';
            if (mimetype.includes('powerpoint') || mimetype.includes('presentation')) return 'bi-file-earmark-ppt text-warning';
            if (mimetype.includes('image')) return 'bi-file-earmark-image text-info';
        }
        
        // Fallback to filename extension
        const extension = filename?.split('.').pop()?.toLowerCase();
        switch (extension) {
            case 'pdf':
                return 'bi-file-earmark-pdf text-danger';
            case 'doc':
            case 'docx':
                return 'bi-file-earmark-word text-primary';
            case 'xls':
            case 'xlsx':
                return 'bi-file-earmark-excel text-success';
            case 'ppt':
            case 'pptx':
                return 'bi-file-earmark-ppt text-warning';
            case 'jpg':
            case 'jpeg':
            case 'png':
            case 'gif':
            case 'webp':
                return 'bi-file-earmark-image text-info';
            case 'txt':
                return 'bi-file-earmark-text text-secondary';
            default:
                return 'bi-file-earmark text-muted';
        }
    };

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'Etc/GMT' };
        return new Date(dateString).toLocaleDateString(LOCALE, options);
    };

    const formatFee = (amount) => Number(amount || 0).toFixed(2);

    const hasAuthToken = () => {
        return Boolean(localStorage.getItem('token'));
    };

    const getRegistrationWindowStatus = () => {
        if (!conference) return { isOpen: false, message: 'Registration unavailable.' };
        if (!conference.registrationEnabled) return { isOpen: false, message: 'Online registration is disabled.' };
        if (conference.registrationDeadline && new Date(conference.registrationDeadline) < new Date()) {
            return { isOpen: false, message: 'Registration deadline has passed.' };
        }
        return { isOpen: true, message: '' };
    };

    const handleOpenRegistration = () => {
        if (!hasAuthToken()) {
            const targetPath = `${location.pathname}${location.search}${location.hash}`;
            localStorage.setItem('postLoginRedirect', targetPath);
            navigate('/login', { state: { from: location }, replace: true });
            return;
        }
        setShowRegistrationModal(true);
    };

    if (loading) {
        return (
            <div className="text-center my-5">
                <Spinner animation="border" role="status" variant="primary">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
                <p className="mt-2">Loading conference details...</p>
            </div>
        );
    }

    if (error) {
        return (
            <Alert variant="danger" className="my-4">
                <Alert.Heading>Error</Alert.Heading>
                <p>{error}</p>
                <div className="d-flex justify-content-end">
                    <Button variant="outline-danger" onClick={() => window.history.back()}>
                        Go Back
                    </Button>
                </div>
            </Alert>
        );
    }

    if (!conference) {
        return (
            <Alert variant="warning" className="my-4">
                <Alert.Heading>Conference Not Found</Alert.Heading>
                <p>The conference you're looking for doesn't exist or has been removed.</p>
                <div className="d-flex justify-content-end">
                    <Link to="/" className="btn btn-outline-primary">
                        Return to Home
                    </Link>
                </div>
            </Alert>
        );
    }

    const feeTiers = [
        { key: 'early', label: 'Early Bird', deadline: conference.fees?.earlyBirdDeadline },
        { key: 'regular', label: 'Regular', deadline: conference.fees?.regularRegistrationDeadline },
        { key: 'late', label: 'Late', deadline: conference.registrationDeadline }
    ].map(tier => ({
        ...tier,
        standard: calculateFeeBreakdown({ feeType: 'standard', fees: conference.fees, tier: tier.key }),
        student: calculateFeeBreakdown({ feeType: 'student', fees: conference.fees, tier: tier.key }),
        accompanied: calculateFeeBreakdown({ feeType: 'standard', fees: conference.fees, tier: tier.key, accompaniedPersonsCount: 1 })
    }));

    return (
        <div className="conference-page py-5">
            <div className="container">
                {/* Conference Header */}
                <div className="mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <h1 className="mb-0">{conference.title}</h1>
                        <Badge bg={getStatusBadgeVariant(conference.status)}>
                            {conference.status}
                        </Badge>
                    </div>
                    <p className="text-muted mb-0">
                        <i className="bi bi-geo-alt-fill me-2"></i>
                        {conference.location}
                    </p>
                    <p className="text-muted">
                        <i className="bi bi-calendar-event me-2"></i>
                        {formatDate(conference.startDate)} - {formatDate(conference.endDate)}
                    </p>
                </div>

                {/* Prominent Program Banner with collapsible program inside */}
                {conference.programHtml && (
                    <Card className="mb-4 border-0 shadow-sm" style={{ backgroundColor: '#f8fafc' }}>
                        <Card.Body className="py-4 d-flex justify-content-between align-items-center">
                            <div>
                                <h4 className="mb-1">Conference Program</h4>
                                {/* <p className="mb-0 text-muted">View the full HTML program for schedule, sessions and speakers.</p> */}
                            </div>
                            <div>
                                <button
                                    type="button"
                                    className="btn btn-primary btn-lg"
                                    onClick={() => setProgramExpanded((p) => !p)}
                                >
                                    <i className="bi bi-calendar-event me-2"></i>
                                    {programExpanded ? 'Hide Program' : 'View Program'}
                                </button>
                            </div>
                        </Card.Body>

                        <Collapse in={programExpanded}>
                            <div id="conference-program">
                                <Card.Body className="pt-3">
                                    <HTMLView
                                        description={conference.programHtml}
                                        files={conference.files}
                                        photos={conference.photos}
                                        endpoint="conferences"
                                    />
                                </Card.Body>
                            </div>
                        </Collapse>
                    </Card>
                )}

                <Row className="g-4">
                    {/* Main Conference Information */}
                    <Col md={8} className="d-flex flex-column">
                        {/* Description */}
                        {conference.description && (
                            <Card className="mb-4" style={{ order: 0 }}>
                                <Card.Header className="bg-conference-primary">
                                    <h3 className="h5 mb-0 text-white">
                                        <i className="bi bi-card-text me-2"></i>
                                        Description
                                    </h3>
                                </Card.Header>
                                <Card.Body>
                                    <HTMLView 
                                        description={conference.description} 
                                        files={conference.files}
                                        photos={conference.photos}
                                        endpoint="conferences"
                                    />
                                </Card.Body>
                            </Card>
                        )}

                        {/* Introduction */}
                        {conference.introduction && (
                            <Card className="mb-4" style={{ order: 91 }}>
                                <Card.Header className="bg-conference-primary">
                                    <h3 className="h5 mb-0 text-white">
                                        <i className="bi bi-info-circle me-2"></i>
                                        Introduction
                                    </h3>
                                </Card.Header>
                                <Card.Body>
                                    <HTMLView 
                                        description={conference.introduction} 
                                        files={conference.files}
                                        photos={conference.photos}
                                        endpoint="conferences"
                                    />
                                </Card.Body>
                            </Card>
                        )}

                        {/* Venue */}
                        {conference.venue && (
                            <Card className="mb-4" style={{ order: 1 }}>
                                <Card.Header className="bg-conference-primary">
                                    <h3 className="h5 mb-0 text-white">
                                        <i className="bi bi-geo-alt me-2"></i>
                                        Venue
                                    </h3>
                                </Card.Header>
                                <Card.Body>
                                    <HTMLView 
                                        description={conference.venue} 
                                        files={conference.files}
                                        photos={conference.photos}
                                        endpoint="conferences"
                                    />
                                </Card.Body>
                            </Card>
                        )}

                        {/* Social Events */}
                        {conference.socialEvents && (
                            <Card className="mb-4" style={{ order: 10 }}>
                                <Card.Header className="bg-conference-primary">
                                    <h3 className="h5 mb-0 text-white">
                                        <i className="bi bi-people me-2"></i>
                                        Social Events
                                    </h3>
                                </Card.Header>
                                <Card.Body>
                                    <HTMLView 
                                        description={conference.socialEvents} 
                                        files={conference.files}
                                        photos={conference.photos}
                                        endpoint="conferences"
                                    />
                                </Card.Body>
                            </Card>
                        )}

                        {/* Accommodation */}
                        {conference.accommodation && (
                            <Card className="mb-4" style={{ order: 12 }}>
                                <Card.Header className="bg-conference-primary">
                                    <h3 className="h5 mb-0 text-white">
                                        <i className="bi bi-building me-2"></i>
                                        Accommodation
                                    </h3>
                                </Card.Header>
                                <Card.Body>
                                    <HTMLView 
                                        description={conference.accommodation} 
                                        files={conference.files}
                                        photos={conference.photos}
                                        endpoint="conferences"
                                    />
                                </Card.Body>
                            </Card>
                        )}

                        {/* Keynote Speakers */}
                        {conference.keynoteSpeakers && (
                            <Card className="mb-4" style={{ order: 4 }}>
                                <Card.Header className="bg-conference-primary">
                                    <h3 className="h5 mb-0 text-white">
                                        <i className="bi bi-mic me-2"></i>
                                        Keynote Speakers
                                    </h3>
                                </Card.Header>
                                <Card.Body>
                                    <HTMLView 
                                        description={conference.keynoteSpeakers} 
                                        files={conference.files}
                                        photos={conference.photos}
                                        endpoint="conferences"
                                    />
                                </Card.Body>
                            </Card>
                        )}

                        {/* Program Chairs */}
                        {conference.programChairs && (
                            <Card className="mb-4" style={{ order: 3 }}>
                                <Card.Header className="bg-conference-primary">
                                    <h3 className="h5 mb-0 text-white">
                                        <i className="bi bi-person-workspace me-2"></i>
                                        Program Chairs
                                    </h3>
                                </Card.Header>
                                <Card.Body>
                                    <HTMLView 
                                        description={conference.programChairs} 
                                        files={conference.files}
                                        photos={conference.photos}
                                        endpoint="conferences"
                                    />
                                </Card.Body>
                            </Card>
                        )}

                        

                        {/* Sponsoring Institutions */}
                        {conference.sponsoringInstitutions && (
                            <Card className="mb-4" style={{ order: 2 }}>
                                <Card.Header className="bg-conference-primary">
                                    <h3 className="h5 mb-0 text-white">
                                        <i className="bi bi-building me-2"></i>
                                        Sponsoring Institutions
                                    </h3>
                                </Card.Header>
                                <Card.Body>
                                    <HTMLView 
                                        description={conference.sponsoringInstitutions} 
                                        files={conference.files}
                                        photos={conference.photos}
                                        endpoint="conferences"
                                    />
                                </Card.Body>
                            </Card>
                        )}

                        {/* Conference Objective */}
                        {conference.conferenceObjective && (
                            <Card className="mb-4" style={{ order: 5 }}>
                                <Card.Header className="bg-conference-primary">
                                    <h3 className="h5 mb-0 text-white">
                                        <i className="bi bi-target me-2"></i>
                                        Conference Objective
                                    </h3>
                                </Card.Header>
                                <Card.Body>
                                    <HTMLView 
                                        description={conference.conferenceObjective} 
                                        files={conference.files}
                                        photos={conference.photos}
                                        endpoint="conferences"
                                    />
                                </Card.Body>
                            </Card>
                        )}

                        {/* Areas of Interest */}
                        {conference.areasOfInterest && (
                            <Card className="mb-4" style={{ order: 6 }}>
                                <Card.Header className="bg-conference-primary">
                                    <h3 className="h5 mb-0 text-white">
                                        <i className="bi bi-lightbulb me-2"></i>
                                        Areas of Interest
                                    </h3>
                                </Card.Header>
                                <Card.Body>
                                    <HTMLView 
                                        description={conference.areasOfInterest} 
                                        files={conference.files}
                                        photos={conference.photos}
                                        endpoint="conferences"
                                    />
                                </Card.Body>
                            </Card>
                        )}

                        {/* Doctoral Student Research Presentations */}
                        {conference.doctoralStudentTutorialsAndPresentations && (
                            <Card className="mb-4" style={{ order: 7 }}>
                                <Card.Header className="bg-conference-primary">
                                    <h3 className="h5 mb-0 text-white">
                                        <i className="bi bi-mortarboard me-2"></i>
                                        Doctoral Student Research Presentations
                                    </h3>
                                </Card.Header>
                                <Card.Body>
                                    <HTMLView 
                                        description={conference.doctoralStudentTutorialsAndPresentations} 
                                        files={conference.files}
                                        photos={conference.photos}
                                        endpoint="conferences"
                                    />
                                </Card.Body>
                            </Card>
                        )}

                        {/* Paper and Thesis Submission Guidelines */}
                        {conference.paperAndThesisSubmissionGuidelines && (
                            <Card className="mb-4" style={{ order: 8 }}>
                                <Card.Header className="bg-conference-primary">
                                    <h3 className="h5 mb-0 text-white">
                                        <i className="bi bi-file-earmark-text me-2"></i>
                                        Paper and Thesis Submission Guidelines
                                    </h3>
                                </Card.Header>
                                <Card.Body>
                                    <HTMLView 
                                        description={conference.paperAndThesisSubmissionGuidelines} 
                                        files={conference.files}
                                        photos={conference.photos}
                                        endpoint="conferences"
                                    />
                                </Card.Body>
                            </Card>
                        )}

                        {/* Paper Awards */}
                        {conference.paperAwards && (
                            <Card className="mb-4" style={{ order: 9 }}>
                                <Card.Header className="bg-conference-primary">
                                    <h3 className="h5 mb-0 text-white">
                                        <i className="bi bi-trophy me-2"></i>
                                        Paper Awards
                                    </h3>
                                </Card.Header>
                                <Card.Body>
                                    <HTMLView 
                                        description={conference.paperAwards} 
                                        files={conference.files}
                                        photos={conference.photos}
                                        endpoint="conferences"
                                    />
                                </Card.Body>
                            </Card>
                        )}

                        {/* Conference Registration Fee */}
                        {(conference.registrationFee || conference.fees) && (
                            <Card className="mb-4" style={{ order: 11 }}>
                                <Card.Header className="bg-conference-primary">
                                    <h3 className="h5 mb-0 text-white">
                                        <i className="bi bi-currency-exchange me-2"></i>
                                        Conference Registration Fee
                                    </h3>
                                </Card.Header>
                                <Card.Body>
                                    {conference.fees && (
                                        <div className="mb-3">
                                            {feeTiers.map(tier => (
                                                <div key={tier.key} className="mb-2">
                                                    <div><strong>{tier.label}:</strong> {formatFee(tier.standard.base)} {tier.standard.currency}</div>
                                                    <div><strong>{tier.label} doctoral student:</strong> {formatFee(tier.student.base)} {tier.student.currency}</div>
                                                    <div><strong>{tier.label} accompanied person:</strong> {formatFee(tier.accompanied.accompaniedFee)} {tier.accompanied.currency}</div>
                                                    {tier.deadline && (
                                                        <div><strong>{tier.label} deadline:</strong> {formatDate(tier.deadline)}</div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {conference.fees?.description && (
                                        <HTMLView
                                            description={conference.fees.description}
                                            files={conference.files}
                                            photos={conference.photos}
                                            endpoint="conferences"
                                        />
                                    )}
                                    {conference.registrationFee && (
                                        <HTMLView
                                            description={conference.registrationFee}
                                            files={conference.files}
                                            photos={conference.photos}
                                            endpoint="conferences"
                                        />
                                    )}
                                </Card.Body>
                            </Card>
                        )}

                        {/* Further Information */}
                        {conference.furtherInformation && (
                            <Card className="mb-4" style={{ order: 93 }}>
                                <Card.Header className="bg-conference-primary">
                                    <h3 className="h5 mb-0 text-white">
                                        <i className="bi bi-info-square me-2"></i>
                                        Further Information
                                    </h3>
                                </Card.Header>
                                <Card.Body>
                                    <HTMLView 
                                        description={conference.furtherInformation} 
                                        files={conference.files}
                                        photos={conference.photos}
                                        endpoint="conferences"
                                    />
                                </Card.Body>
                            </Card>
                        )}

                        {/* Scientific Committee */}
                        {conference.scientificCommittee && (
                            <Card className="mb-4" style={{ order: 13 }}>
                                <Card.Header className="bg-conference-primary">
                                    <h3 className="h5 mb-0 text-white">
                                        <i className="bi bi-people me-2"></i>
                                        Scientific Committee
                                    </h3>
                                </Card.Header>
                                <Card.Body>
                                    <HTMLView 
                                        description={conference.scientificCommittee} 
                                        files={conference.files}
                                        photos={conference.photos}
                                        endpoint="conferences"
                                    />
                                </Card.Body>
                            </Card>
                        )}

                        {/* Local Organizing Committee */}
                        {conference.localOrganizingCommittee && (
                            <Card className="mb-4" style={{ order: 14 }}>
                                <Card.Header className="bg-conference-primary">
                                    <h3 className="h5 mb-0 text-white">
                                        <i className="bi bi-person-lines-fill me-2"></i>
                                        Local Organizing Committee
                                    </h3>
                                </Card.Header>
                                <Card.Body>
                                    <HTMLView 
                                        description={conference.localOrganizingCommittee} 
                                        files={conference.files}
                                        photos={conference.photos}
                                        endpoint="conferences"
                                    />
                                </Card.Body>
                            </Card>
                        )}
                    </Col>

                    {/* Sidebar */}
                    <Col md={4}>
                        {/* Conference Files */}
                        <Card className="mb-4">
                                <Card.Header className="bg-conference-secondary">
                                    <h3 className="h5 mb-0 text-white">
                                    <i className="bi bi-folder me-2"></i>
                                    Brochure, Sessions, and Booklet
                                </h3>
                            </Card.Header>
                            <Card.Body>
                                {conference.files && conference.files.length > 0 ? (
                                    <div className="list-group list-group-flush">
                                        {conference.files.map((file, index) => (
                                            <div key={index} className="list-group-item d-flex justify-content-between align-items-center px-0">
                                                <div className="d-flex align-items-center">
                                                    <i className={`${getFileIcon(file.filename || file.originalName, file.mimetype)} me-2 fs-3`}></i>
                                                    <div>
                                                        <div className="fw-medium">
                                                            {file.originalName || file.filename}
                                                        </div>
                                                        {file.size && (
                                                            <small className="text-muted">
                                                                {(file.size / 1024).toFixed(1)} KB
                                                            </small>
                                                        )}
                                                    </div>
                                                </div>
                                                <a 
                                                    href={`${baseUrl}/conferences/files/${file.filename}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="btn btn-sm btn-outline-primary"
                                                    title="Download file"
                                                >
                                                    <i className="bi bi-download"></i>
                                                </a>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-muted mb-0">
                                        <i className="bi bi-info-circle me-2"></i>
                                        No files available for this conference.
                                    </p>
                                )}
                            </Card.Body>
                        </Card>

                        {/* Actions */}
                        <Card className="mb-4">
                                <Card.Header className="bg-conference-secondary">
                                    <h3 className="h5 mb-0 text-white">
                                        <i className="bi bi-info-circle me-2"></i>
                                        Actions
                                    </h3>
                            </Card.Header>
                            <Card.Body>
                                <div className="d-grid gap-2">
                                    {conference.status === 'Open' && (
                                        <Link to="/submissions" className="btn btn-primary">
                                            <i className="bi bi-file-earmark-text me-2"></i>Submit Manuscript
                                        </Link>
                                    )}
                                    {(() => {
                                        const registrationWindow = getRegistrationWindowStatus();
                                        if (!registrationWindow.isOpen) {
                                            return (
                                                <button className="btn btn-outline-secondary" disabled title={registrationWindow.message}>
                                                    <i className="bi bi-lock me-2"></i>
                                                    Attendee Registration Closed
                                                </button>
                                            );
                                        }
                                        // return (
                                        //     <button className="btn btn-success" onClick={handleOpenRegistration}>
                                        //         <i className="bi bi-person-check me-2"></i>
                                        //          Register as NON-presenting participant
                                        //     </button>
                                        // );
                                    })()}
                                    <AddToCalendarComponent
                                        title={conference.title}
                                        description={`${conference.title} - ${conference.location}`}
                                        startDate={conference.startDate}
                                        endDate={conference.endDate}
                                        location={conference.location}
                                        type="conference"
                                    />
                                    {conference.programHtml && (
                                        <button
                                            type="button"
                                            className="btn btn-outline-primary"
                                            onClick={() => setProgramExpanded((p) => !p)}
                                        >
                                            <i className="bi bi-calendar-event me-2"></i>
                                            {programExpanded ? 'Hide Program' : 'View Program'}
                                        </button>
                                    )}
                                    <ShareComponent
                                        title={conference.title}
                                        description={`Join us for ${conference.title} in ${conference.location}`}
                                        type="conference"
                                    />
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* Conference Photos Gallery at the bottom */}
                {conference.photos && conference.photos.length > 0 && (
                    <Row className="mt-5">
                        <Col>
                            <Card>
                                <Card.Header className="bg-conference-primary">
                                    <h3 className="h5 mb-0 text-white">
                                        <i className="bi bi-images me-2"></i>
                                        Conference Gallery ({conference.photos.length} photos)
                                    </h3>
                                </Card.Header>
                                <Card.Body className="p-4">
                                    <div 
                                        id={`carousel-${conference._id}`} 
                                        className="carousel slide" 
                                        data-bs-ride="carousel"
                                    >
                                        {/* Carousel Indicators */}
                                        <div className="carousel-indicators">
                                            {Array.from({ length: Math.ceil(conference.photos.length / 6) }).map((_, slideIndex) => (
                                                <button
                                                    key={slideIndex}
                                                    type="button"
                                                    data-bs-target={`#carousel-${conference._id}`}
                                                    data-bs-slide-to={slideIndex}
                                                    className={slideIndex === 0 ? 'active' : ''}
                                                    aria-current={slideIndex === 0 ? 'true' : 'false'}
                                                    aria-label={`Slide ${slideIndex + 1}`}
                                                ></button>
                                            ))}
                                        </div>

                                        {/* Carousel Inner */}
                                        <div className="carousel-inner">
                                            {Array.from({ length: Math.ceil(conference.photos.length / 6) }).map((_, slideIndex) => {
                                                const startIndex = slideIndex * 6;
                                                const slidePhotos = conference.photos.slice(startIndex, startIndex + 6);
                                                
                                                return (
                                                    <div 
                                                        key={slideIndex} 
                                                        className={`carousel-item ${slideIndex === 0 ? 'active' : ''}`}
                                                    >
                                                        <div className="row g-3">
                                                            {slidePhotos.map((photo, photoIndex) => (
                                                                <div key={startIndex + photoIndex} className="col-lg-2 col-md-3 col-sm-4 col-6">
                                                                    <div 
                                                                        className="photo-container position-relative overflow-hidden rounded shadow-sm"
                                                                        style={{ aspectRatio: '1:1' }}
                                                                        onClick={() => handleImageClick(photo, startIndex + photoIndex)}

                                                                    >
                                                                        <img
                                                                            src={`${baseUrl}/conferences/photos/${photo.filename}`}
                                                                            alt={photo.originalName || `Conference photo ${startIndex + photoIndex + 1}`}
                                                                            className="img-fluid w-100 h-100 photo-thumbnail"
                                                                            style={{ 
                                                                                objectFit: 'cover',
                                                                                cursor: 'pointer',
                                                                                transition: 'transform 0.3s ease'
                                                                            }}
                                                                            // onClick={() => handleImageClick(photo)}
                                                                            onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
                                                                            onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                                                                            title="Click to view full size"
                                                                        />
                                                                        <div 
                                                                            className="photo-overlay position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                                                                            style={{
                                                                                background: 'rgba(0,0,0,0.5)',
                                                                                opacity: 0,
                                                                                transition: 'opacity 0.3s ease'
                                                                            }}
                                                                            onMouseEnter={(e) => e.target.style.opacity = 1}
                                                                            onMouseLeave={(e) => e.target.style.opacity = 0}
                                                                        >
                                                                            <i className="bi bi-zoom-in text-white fs-3"></i>
                                                                        </div>
                                                                    </div>
                                                                    {photo.originalName && (
                                                                        <small className="text-muted d-block mt-1 text-center text-truncate">
                                                                            {photo.originalName}
                                                                        </small>
                                                                    )}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        {/* Carousel Controls */}
                                        {conference.photos.length > 6 && (
                                            <>
                                                <button 
                                                    className="carousel-control-prev" 
                                                    type="button" 
                                                    data-bs-target={`#carousel-${conference._id}`} 
                                                    data-bs-slide="prev"
                                                    style={{ width: '50px' }}
                                                >
                                                    <span className="carousel-control-prev-icon bg-dark rounded-circle p-3" aria-hidden="true"></span>
                                                    <span className="visually-hidden">Previous</span>
                                                </button>
                                                <button 
                                                    className="carousel-control-next" 
                                                    type="button" 
                                                    data-bs-target={`#carousel-${conference._id}`} 
                                                    data-bs-slide="next"
                                                    style={{ width: '50px' }}
                                                >
                                                    <span className="carousel-control-next-icon bg-dark rounded-circle p-3" aria-hidden="true"></span>
                                                    <span className="visually-hidden">Next</span>
                                                </button>
                                            </>
                                        )}
                                    </div>

                                    {/* Gallery Info */}
                                    <div className="text-center mt-3">
                                        <small className="text-muted">
                                            <i className="bi bi-info-circle me-1"></i>
                                            Click on any image to view in full size
                                            {conference.photos.length > 6 && ` • Showing ${Math.min(6, conference.photos.length)} of ${conference.photos.length} photos per slide`}
                                        </small>
                                        <div className="mt-3">
                                            <Link to={`/conference/${conference._id}/gallery`} className="btn btn-outline-primary btn-sm">
                                                <i className="bi bi-images me-2"></i>
                                                Open full gallery page
                                            </Link>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                )}
            </div>

            {/* Image Modal */}
            <Modal 
                show={showImageModal} 
                onHide={closeImageModal} 
                size="xl" 
                centered
                className="image-modal"
            >
                <Modal.Header closeButton className="border-0">
                    <Modal.Title>
                        {selectedImage?.originalName || 'Conference Photo'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-0 text-center bg-dark">
                    {selectedImage && (
                        <img
                            src={`${baseUrl}/conferences/photos/${selectedImage.filename}`}
                            alt={selectedImage.originalName || 'Conference photo'}
                            className="img-fluid"
                            style={{ 
                                maxHeight: '80vh',
                                width: 'auto',
                                maxWidth: '100%'
                            }}
                        />
                    )}
                </Modal.Body>
                <Modal.Footer className="border-0 justify-content-between">
                    <div className="d-flex align-items-center gap-2">
                        {selectedImage?.originalName && (
                            <small className="text-muted">
                                <i className="bi bi-image me-2"></i>
                                {selectedImage.originalName}
                            </small>
                        )}
                        {galleryPhotos.length > 0 && (
                            <small className="text-muted">
                                {selectedImageIndex + 1} / {galleryPhotos.length}
                            </small>
                        )}
                    </div>
                    <div className="d-flex gap-2">
                        <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleNavigateImage(-1)}
                            disabled={galleryPhotos.length <= 1}
                        >
                            <i className="bi bi-chevron-left me-1"></i>
                            Previous
                        </Button>
                        <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleNavigateImage(1)}
                            disabled={galleryPhotos.length <= 1}
                        >
                            Next
                            <i className="bi bi-chevron-right ms-1"></i>
                        </Button>
                        <Button 
                            variant="outline-primary"
                            size="sm"
                            onClick={() => {
                                const link = document.createElement('a');
                                link.href = `${baseUrl}/conferences/photos/${selectedImage.filename}`;
                                link.download = selectedImage.originalName || selectedImage.filename;
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                            }}
                            title="Download image"
                        >
                            <i className="bi bi-download me-1"></i>
                            Download
                        </Button>
                        <Button 
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => {
                                const url = `${window.location.origin}${baseUrl}/conferences/photos/${selectedImage.filename}`;
                                navigator.clipboard.writeText(url).then(() => {
                                    alert('Image URL copied to clipboard!');
                                }).catch(() => {
                                    alert('Failed to copy URL');
                                });
                            }}
                            title="Copy image URL"
                        >
                            <i className="bi bi-link-45deg me-1"></i>
                            Copy URL
                        </Button>
                        <Button 
                            variant="secondary"
                            size="sm"
                            onClick={closeImageModal}
                        >
                            Close
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal>

            {showRegistrationModal && (
                <AttendeeRegistrationModal
                    conference={conference}
                    onClose={() => setShowRegistrationModal(false)}
                    onSuccess={() => setShowRegistrationModal(false)}
                />
            )}
        </div>
    );
};

export default Conference;